import { db } from "@/firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  collectionGroup,
} from "firebase/firestore";
import { fetchData } from "../supabase/functions";
import { supabase } from "@/supabase/client";

export const getAuditTableDocumentsForUser = async (
  userId,
  limitCount,
  startAfterDoc
) => {
  try {
    if (!userId) {
      throw new Error("User ID is required");
    }

    // Calculate date 30 days ago
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const thirtyDaysAgoISO = thirtyDaysAgo.toISOString();

    // Build the main query for paginated data with 30-day filter
    const query = {
      filter: { uid: userId },
      order: { created_at: "desc" },
      limit: limitCount,
    };

    if (startAfterDoc) {
      query.offset = startAfterDoc;
    }

    // Add date filter for last 30 days
    const dateFilters = {
      created_at: { gt: thirtyDaysAgoISO }
    };

    // Fetch paginated data with date filter
    const { data, error } = await fetchData("sys_audit", query, dateFilters);

    if (error) {
      throw new Error(`Error fetching audit table documents: ${error}`);
    }

    // For count, use a more efficient approach with count query and date filter
    const { count, error: countError } = await supabase
      .from("sys_audit")
      .select("*", { count: "exact", head: true })
      .eq("uid", userId)
      .gt("created_at", thirtyDaysAgoISO)
      .abortSignal(AbortSignal.timeout(15000)); // 15 second timeout for count query

    if (countError) {
      console.warn("Error fetching count, using data length as fallback:", countError);
      // Fallback: use the length of current data if count query fails
      const count = data ? data.length : 0;
    }

    const lastDocument = data && data.length > 0 ? data[data.length - 1] : null;

    return { 
      data: data || [], 
      lastDocument, 
      count: count || 0 
    };
  } catch (error) {
    console.error("Error in getAuditTableDocumentsForUser:", error);
    return { 
      data: [], 
      lastDocument: null, 
      count: 0, 
      error: error.message 
    };
  }
};

export const getUsersNodesReferralFirebase = async (
  referralFromUid,
  limitCount,
  startAfterDoc
) => {
  //console.log("&&&&&&& REFERRAL FROM UID " + referralFromUid);
  //console.log("&&&&&&& START AFTER " + startAfterDoc);

  // Create the base query
  let userAuditQuery = query(
    collection(db, "referral_payments"),
    where("referral_from_uid", "==", referralFromUid),
    orderBy("referral_date", "asc"),
    limit(limitCount)
  );

  let userAuditQueryCount = query(
    collection(db, "referral_payments"),
    where("referral_from_uid", "==", referralFromUid),
    orderBy("referral_date", "asc")
  );

  // If startAfterDoc is provided, use it in the query
  if (startAfterDoc) {
    userAuditQuery = query(userAuditQuery, startAfter(startAfterDoc));
  }
  const snapshotcount = await getDocs(userAuditQueryCount);
  //console.log("total size " + snapshotcount.size);

  const snapshot = await getDocs(userAuditQuery);
  const lastDocument = snapshot.docs[snapshot.docs.length - 1];
  const data = snapshot.docs.map((doc) => doc.data());
  //console.log({ data, lastDocument });
  return { data, lastDocument, count: snapshotcount.size };
};

export const getUsersNodesReferral = async (
  referralFromUid,
  limitCount,
  startAfterDate
) => {
  try {
    // console.log("📡 Fetching referral data for:", referralFromUid);

    let query = supabase
      .from("products_referral")
      .select("*")
      .eq("referral_from_uid", referralFromUid)
      .order("created_at", { ascending: true })
      .limit(limitCount);

    if (startAfterDate) {
      query = query.gt("created_at", startAfterDate);
    }

    const { data, error, count } = await query;

    if (error) throw error;
    // console.log("✅ Data fetched successfully:", data.length, "records found.");

    return { data, lastDocument: data[data.length - 1], count };
  } catch (error) {
    console.error("❌ Error fetching referral data:", error);
    return { data: [], lastDocument: null, count: 0 };
  }
};

export const getUsersNodesPaginated = async (
  wallet,
  contractAddress,
  limitCount,
  startAfterDoc
) => {
  // console.log(
  //   `Fetching nodes for wallet: ${wallet}, contractAddress: ${contractAddress}`
  // );

  // Reference to the new path structure for NFT tokens
  const tokenRef = collection(
    db,
    "nfts",
    "ecosystem",
    "contracts",
    contractAddress,
    "tokens"
  );

  // Create the base query to filter by current_owner (instead of uid)
  let userAuditQuery = query(
    tokenRef,
    where("current_owner", "==", wallet), // Replace uid with current_owner and userId with wallet
    orderBy("purchase_date_time", "desc"),
    limit(limitCount)
  );

  let userAuditQueryCount = query(
    tokenRef,
    where("current_owner", "==", wallet), // Replace uid with current_owner
    orderBy("purchase_date_time", "desc")
  );

  // If startAfterDoc is provided, apply pagination
  if (startAfterDoc) {
    userAuditQuery = query(userAuditQuery, startAfter(startAfterDoc));
  }

  // Fetch total count of documents matching the query
  const snapshotCount = await getDocs(userAuditQueryCount);
  // console.log("Total size:", snapshotCount.size);

  // Fetch the paginated documents
  const snapshot = await getDocs(userAuditQuery);
  const lastDocument = snapshot.docs[snapshot.docs.length - 1];
  const data = snapshot.docs.map((doc) => doc.data());

  // console.log({ data, lastDocument });

  // Return the data, lastDocument for pagination, and the total count
  return { data, lastDocument, count: snapshotCount.size };
};

export const getUsersUnmintedTokensPaginated = async (
  wallet,
  license,
  limitCount,
  startAfterDoc
) => {
  // console.log(
  //   `Fetching unminted tokens for wallettttt: ${wallet}, license: ${license}`
  // );

  const full = process.env.NEXT_PUBLIC_CONTRACT_ADDRESS_FOR_FULL_NODE_MINTING;
  const fractional =
    process.env.NEXT_PUBLIC_CONTRACT_ADDRESS_FOR_FRACTIONAL_NODE_MINTING;

  // Determine the contract address based on the license type
  const contractAddress = license === "full" ? full : fractional;

  // Reference to the collection for the specified contract address
  const tokenRef = collection(
    db,
    "nfts",
    "ecosystem",
    "contracts",
    contractAddress,
    "tokens"
  );

  // Create the query to fetch unminted tokens for the specified wallet
  const userAuditQuery = query(
    tokenRef,
    where("current_owner", "==", wallet),
    where("mint_status", "==", "not_minted")
  );

  try {
    // Fetch all the matching documents
    const snapshot = await getDocs(userAuditQuery);

    // Map the documents to their data
    const data = snapshot.docs.map((doc) => doc.data());

    // console.log(data);

    // Return the data
    return {
      data,
      count: snapshot.size, // Return the number of unminted tokens
    };
  } catch (error) {
    console.error("Error fetching unminted tokens: ", error);
    throw error;
  }
};

export const getUsersUnMintedTokenPaginated = async (wallet, license) => {
  try {
    console.log("Querying Supabase for nodes to mint...");
    // console.log("License ", license);
    const { data, error } = await supabase
      .from("products_nft_mint")
      .select("*")
      .eq("mint_status", "not_minted")
      .eq("type", license)
      .eq("purchaser_wallet_lc", wallet.toLowerCase());

    if (error) {
      console.error("Error fetching nodes from Supabase:", error.message);
      throw new Error(error.message);
    }

    // Fetch additional details for each row
    const detailedData = await Promise.all(
      data.map(async (row) => {
        const { data: productData, error: productError } = await supabase
          .from("products_purchased")
          .select("discount, payment_scan_link, price, referral_uid")
          .eq("purchase_id", row.products_purchased_id)
          .single();

        if (productError) {
          console.error(
            `Error fetching product details for id ${row.products_purchased_id}:`,
            productError.message
          );
          throw new Error(productError.message);
        }
        // console.log(productData);
        return {
          ...row,
          discount: productData.discount,
          payment_scan_link: productData.payment_scan_link,
          price: productData.price,
          referrer_uid: productData.referrer_uid,
        };
      })
    );

    // console.log(detailedData.length);
    // console.log(detailedData);

    return {
      data: detailedData,
      count: detailedData.length,
    };
  } catch (error) {
    console.log(error);
    throw error; // Rethrow the error to propagate it to the caller
  }
};

export const getTickerData = async () => {
  try {
    const { data, error } = await fetchData("website_ticker_data", {
      select: "*", // Select all fields
      order: { created_at: "desc" },
    });

    if (error) {
      throw new Error(`Error fetching ticker data: ${error.message}`);
    }

    // console.log(data)

    return data;
  } catch (error) {
    console.error("Error fetching ticker data:", error);
    return null; // Return null in case of error
  }
};

export const verifyAdminCredentials = async (adminWallet, accessCode) => {
  try {
    const adminQuerySnapshot = await getDocs(
      query(
        collection(db, "admin_login"),
        where("wallet", "==", adminWallet),
        where("access_code", "==", accessCode)
      )
    );

    if (adminQuerySnapshot.empty) {
      console.log("Invalid wallet or access code.");
      return false; // Return false if no matching document is found
    }

    console.log("Admin credentials verified.");
    return true; // Return true if matching document is found
  } catch (error) {
    console.error("Error verifying admin credentials: ", error);
    throw error;
  }
};

export const extractChainId = (eipString) => {
  // Check if eipString is undefined or null
  if (!eipString) {
    return null; // or throw an error, depending on your use case
  }

  // Split the string by colons
  const parts = eipString.split(":");

  // Check if the parts array has at least two elements
  if (parts.length < 2) {
    return null; // or throw an error, depending on your use case
  }

  // The chain ID is the second part (index 1)
  const chainId = parts[1];
  return chainId;
};

export const getNFTContractDetails = async (contractAddress, tokenId) => {
  try {
    // console.log({ contractAddress, tokenId });
    const querySnapshot = await getDocs(
      query(
        collection(db, `nfts/ecosystem/contracts/`),
        where("address", "==", contractAddress)
      )
    );

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const nftData = { id: doc.id, ...doc.data() };
      // console.log("NFT Contract Data: ", nftData);
      return nftData;
    } else {
      console.log("No contract found with that address.");
      return null;
    }
  } catch (error) {
    console.error("Error fetching NFT contract data:", error);
    return null;
  }
};

/**
 * Fetches the claim button status for a specific wallet address.
 *
 * @param {string} wallet - The wallet address to check.
 * @returns {Promise<Object|null>} The response data or null if an error occurs.
 */
export const fetchClaimButtonStatus = async (wallet) => {
  try {
    const response = await fetch(
      `https://us-central1-prodmxs.cloudfunctions.net/claim_button_check?wallet=${wallet}`,
      {
        method: "GET",
        headers: {
          Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532",
        },
      }
    );

    if (!response.ok) {
      console.error("Error fetching claim button status:", response.statusText);
      return null;
    }

    const data = await response.json();
    return data.success ? data : null;
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
};

/**
 * Fetches the reward generation status.
 *
 * @returns {Promise<Object|null>} The response data or null if an error occurs.
 */
export const fetchClaimGenarationStatus = async () => {
  try {
    const response = await fetch(
      `https://us-central1-prodmxs.cloudfunctions.net/claims_gene_status`,
      {
        method: "GET",
        headers: {
          Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532",
        },
      }
    );

    if (!response.ok) {
      console.error(
        "Error fetching generation status:",
        response.statusText,
        response.status
      );
      return null;
    }

    const data = await response.json();
    // console.log("Generation Status Response:", data);
    return data;
  } catch (error) {
    console.error("Error fetching generation status:", error);
    return null;
  }
};

/**
 * Fetches user loyalty points for a specific wallet_lc.
 *
 * @param {string} walletLc - The wallet address to query loyalty points for.
 * @returns {Promise<Object|null>} The response data or null if an error occurs.
 */
export const fetchUserLoyaltyPoints = async (wallet) => {
  try {
    if (!wallet) {
      console.error("wallet is required to fetch loyalty points.");
      return null;
    }

    const url = `https://us-central1-prodmxs.cloudfunctions.net/get_users_loyalty_points?wallet=${wallet}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532",
      },
    });

    if (!response.ok) {
      console.error(
        "Error fetching user loyalty points:",
        response.statusText,
        response.status
      );
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching user loyalty points:", error);
    return null;
  }
};

/**
 * Fetches user loyalty history for a specific wallet.
 *
 * @param {string} wallet - The wallet address to fetch history for.
 * @returns {Promise<Object|null>} The response data or null if an error occurs.
 */
export const fetchUserLoyaltyHistory = async (wallet) => {
  try {
    if (!wallet) {
      console.error("wallet is required to fetch loyalty history.");
      return null;
    }

    const url = `https://us-central1-prodmxs.cloudfunctions.net/get_user_history?wallet=${wallet}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532",
      },
    });

    if (!response.ok) {
      console.error(
        "Error fetching user loyalty history:",
        response.statusText,
        response.status
      );
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching user loyalty history:", error);
    return null;
  }
};

/**
 * Fetches node hosting data for a specific wallet.
 *
 * @param {string} wallet - The wallet address to query node hosting data for.
 * @param {number} [page=1] - The page number for pagination.
 * @param {number} [limit=10] - The number of items per page.
 * @param {string} [view='credit'] - The view type ('credit' or 'debit').
 * @returns {Promise<Object|null>} The response data or null if an error occurs.
 */
export const fetchNodeHostingData = async (wallet, page = 1, limit = 10, view = 'credit') => {
  try {
    if (!wallet) {
      console.error("wallet is required to fetch node hosting data.");
      return null;
    }

    // Ensure the limit is exactly 10 per page
    const fixedLimit = 10;
    
    // Add the view parameter to the URL to support credit vs debit filtering
    const url = `https://us-central1-prodmxs.cloudfunctions.net/get_node_hosting_data?wallet=${wallet}&page=${page}&limit=${fixedLimit}&view=${view}`;

    // console.log(`Fetching node hosting data: wallet=${wallet}, page=${page}, limit=${fixedLimit}, view=${view}`);
    
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532", 
      },
    });

    if (!response.ok) {
      console.error(
        "Error fetching node hosting data:",
        response.statusText,
        response.status
      );
      return null;
    }

    const data = await response.json();
    
    // Log pagination details to help debug
    if (data.success && data.data && data.data.pagination) {
      const { pagination } = data.data;
      // console.log(`Pagination: page=${pagination.page}, total_pages=${pagination.total_pages}`);
      // console.log(`Items: credit=${pagination.total_credit_items}, debit=${pagination.total_debit_items}`);
      
      // Calculate correct pages for debugging
      const creditPages = Math.ceil(pagination.total_credit_items / pagination.limit);
      const debitPages = Math.ceil(pagination.total_debit_items / pagination.limit);
      // console.log(`Actual pages: credit=${creditPages}, debit=${debitPages}`);
    }
    
    return data;
  } catch (error) {
    console.error("Error fetching node hosting data:", error);
    return null;
  }
};

/**
 * Fetches sent notifications for node hosting for a specific wallet.
 * 
 * @param {string} wallet - The wallet address to query sent notifications for.
 * @param {number} [page=1] - Page number for paginated results.
 * @param {number} [limit=20] - Number of items per page.
 * @returns {Promise<Object|null>} The response data or null if an error occurs.
 */
export const fetchNodeHostingSentNotifications = async (wallet, page = 1, limit = 20) => {
  try {
    if (!wallet) {
      console.error("wallet is required to fetch sent notifications.");
      return null;
    }

    const url = `https://us-central1-prodmxs.cloudfunctions.net/node_hosting_sent_notifications?wallet=${wallet}&page=${page}&limit=${limit}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532", 
      },
    });

    if (!response.ok) {
      console.error(
        "Error fetching sent notifications:",
        response.statusText,
        response.status
      );
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching sent notifications:", error);
    return null;
  }
};

/**
 * Deletes node hosting sent notifications by their IDs.
 *
 * @param {number[]} ids - Array of notification IDs to delete.
 * @returns {Promise<{ success: boolean; deletedCount?: number; message?: string; error?: string }>}
 */
export const deleteUserNodeHostingNotifications = async (ids) => {
  try {
    if (!Array.isArray(ids) || ids.length === 0) {
      throw new Error("You must provide an array of notification IDs to delete.");
    }

    const response = await fetch(
      "https://us-central1-prodmxs.cloudfunctions.net/delete_user_node_hosting_notifications",
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532", 
        },
        body: JSON.stringify({ ids }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      console.error("Error deleting notifications:", data?.error || response.statusText);
      return {
        success: false,
        error: data?.error || "Failed to delete notifications",
      };
    }

    return {
      success: true,
      message: data.message,
      deletedCount: data.deletedCount,
    };
  } catch (error) {
    console.error("Error calling delete API:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

/**
 * Fetches wallet reward generation fee reports for a specific wallet.
 *
 * @param {string} wallet - The wallet address to query reports for.
 * @param {number} [page=1] - Page number for paginated results.
 * @param {number} [limit=20] - Number of items per page.
 * @returns {Promise<Object|null>} The response data or null if an error occurs.
 */
export const fetchWalletReports = async (wallet, page = 1, limit = 20) => {
  try {
    if (!wallet) {
      console.error("wallet is required to fetch wallet reports.");
      return null;
    }

    const url = `https://us-central1-prodmxs.cloudfunctions.net/get_wallet_reports?wallet=${wallet}&page=${page}&limit=${limit}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532", 
      },
    });

    if (!response.ok) {
      console.error(
        "Error fetching wallet reports:",
        response.statusText,
        response.status
      );
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching wallet reports:", error);
    return null;
  }
};

// Function to fetch announcements
export const fetchAnnouncements = async () => {
  try {
    const response = await fetch('https://us-central1-prodmxs.cloudfunctions.net/get_annoucement', {
      method: "GET",
      headers: {
        Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532",
      },
    });
    
    if (!response.ok) {
      throw new Error(`Error fetching announcements: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching announcements:', error);
    return { success: false, error: error.message };
  }
};

// Function to mark announcements as dismissed
export const dismissAnnouncements = async (announcementIds) => {
  try {
    if (!announcementIds || announcementIds.length === 0) {
      return { success: false, error: 'No announcement IDs provided' };
    }
    
    // Will need to be implemented on the backend
    // This is a placeholder for future implementation
    // When the API endpoint is available, replace this with the actual call
    
    // console.log('Dismissing announcements:', announcementIds);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return { 
      success: true, 
      message: 'Announcements marked as dismissed',
      dismissedCount: announcementIds.length
    };
  } catch (error) {
    console.error('Error dismissing announcements:', error);
    return { success: false, error: error.message };
  }
};

// Function to check hosting credit status
export const checkHostingCreditStatus = async (wallet) => {
  try {
    if (!wallet) {
      throw new Error('Wallet address is required');
    }

    const response = await fetch(`https://us-central1-prodmxs.cloudfunctions.net/check_hosting_credit_status?wallet=${wallet}`, {
      method: "GET",
      headers: {
        Authorization: "c11776f9-34d0-4f51-73c3-7caa78fbd532",
      },
    });
    
    if (!response.ok) {
      throw new Error(`Error checking hosting credit status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error checking hosting credit status:', error);
    return { success: false, error: error.message };
  }
};




